/* v1.4.3: independent page controls; shopping and celebration handlers stay separate. */
(() => {
  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-contact-routing]').forEach(section => {
      if (section.dataset.routingReady) return;
      section.dataset.routingReady = 'true';
      const select = section.querySelector('[name="contact[topic]"]');
      const field = section.querySelector('[data-order-field]');
      if (!select || !field || section.dataset.orderMode !== 'conditional') return;
      const topics = section.dataset.orderTopics.split(',').map(s => s.trim().toLocaleLowerCase()).filter(Boolean);
      const update = () => {
        const show = topics.includes(select.value.trim().toLocaleLowerCase());
        field.hidden = !show;
        field.querySelector('input').disabled = !show;
      };
      select.addEventListener('change', update);
      update();
    });
    scope.querySelectorAll('[data-desktop-menu]').forEach(menu => {
      if (menu.dataset.menuReady) return;
      menu.dataset.menuReady = 'true';
      const item = menu.closest('.bf-nav-v143__item');
      const panel = menu.querySelector('.bf-nav-v143__panel');
      const summary = menu.querySelector('summary');
      let hoverTimer;
      let editorSelected = false;
      const sync = () => {
        if (panel) { panel.hidden = !menu.open; panel.inert = !menu.open; }
        summary?.setAttribute('aria-expanded', String(menu.open));
      };
      const setOpen = (open) => { menu.open = open; sync(); };
      const keyboardFocusInside = () => menu.contains(document.activeElement) && document.activeElement.matches(':focus-visible');
      sync();
      const hoverEnabled = () => menu.closest('[data-menu-behavior]')?.dataset.menuBehavior === 'hover' && matchMedia('(hover: hover) and (pointer: fine)').matches;
      item?.addEventListener('pointerenter', event => {
        if (!hoverEnabled() || event.pointerType !== 'mouse') return;
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => { if (menu.isConnected) setOpen(true); }, 140);
      });
      item?.addEventListener('pointerleave', event => {
        if (!hoverEnabled() || event.pointerType !== 'mouse') return;
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => { if (!editorSelected && !keyboardFocusInside()) setOpen(false); }, 240);
      });
      summary?.addEventListener('click', () => clearTimeout(hoverTimer));

      menu.addEventListener('toggle', () => {
        sync();
        if (!menu.open) return;
        document.querySelectorAll('[data-desktop-menu][open]').forEach(other => { if (other !== menu) other.open = false; });
      });
      menu.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        clearTimeout(hoverTimer); editorSelected = false;
        event.preventDefault(); event.stopPropagation(); setOpen(false); summary?.focus();
      });
      menu.addEventListener('focusout', event => {
        if (event.relatedTarget && !menu.contains(event.relatedTarget) && !editorSelected) setOpen(false);
      });
      menu.addEventListener('shopify:block:select', () => {
        clearTimeout(hoverTimer); editorSelected = true; setOpen(true);
      });
      menu.addEventListener('shopify:block:deselect', () => {
        editorSelected = false;
        if (!item?.matches(':hover') && !keyboardFocusInside()) setOpen(false);
      });
    });
  };
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('click', event => {
    document.querySelectorAll('[data-desktop-menu][open]').forEach(menu => {
      if (!menu.contains(event.target) || event.target.closest('a')) menu.open = false;
    });
  });

  let privacyRequest;
  const loadPrivacy = () => {
    if (window.Shopify?.customerPrivacy) return Promise.resolve(window.Shopify.customerPrivacy);
    if (privacyRequest) return privacyRequest;
    privacyRequest = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Privacy controls unavailable')), 8000);
      if (!window.Shopify?.loadFeatures) { clearTimeout(timeout); reject(new Error('Shopify unavailable')); return; }
      window.Shopify.loadFeatures([{name:'consent-tracking-api', version:'0.1'}], error => {
        clearTimeout(timeout);
        if (error || !window.Shopify.customerPrivacy) reject(error || new Error('Privacy controls unavailable'));
        else resolve(window.Shopify.customerPrivacy);
      });
    }).catch(error => { privacyRequest = null; throw error; });
    return privacyRequest;
  };
  let privacyOpener;
  let privacyGeneration = 0;
  document.addEventListener('click', async event => {
    const opener = event.target.closest('[data-privacy-open]');
    const closer = event.target.closest('[data-privacy-close]');
    const dialog = document.querySelector('#BellyfulPrivacyPreferences');
    if (!dialog || (!opener && !closer)) return;
    if (closer) { dialog.close(); return; }
    privacyOpener = opener;
    const generation = ++privacyGeneration;
    const status = dialog.querySelector('[data-privacy-status]');
    const fieldset = dialog.querySelector('fieldset');
    fieldset.disabled = true;
    status.textContent = dialog.dataset.loading;
    dialog.showModal();
    if (!dialog.dataset.closeReady) {
      dialog.dataset.closeReady = 'true';
      dialog.addEventListener('close', () => { privacyGeneration++; privacyOpener?.focus(); });
    }
    try {
      const api = await loadPrivacy();
      if (!dialog.open || generation !== privacyGeneration) return;
      const consent = api.currentVisitorConsent();
      dialog.querySelectorAll('input[type=checkbox]').forEach(input => {
        input.checked = consent[input.name] === 'yes';
        input.disabled = input.name === 'sale_of_data' && navigator.globalPrivacyControl === true;
        if (input.disabled) input.checked = false;
      });
      dialog.querySelector('[data-privacy-gpc]').hidden = navigator.globalPrivacyControl !== true;
      fieldset.disabled = false;
      status.textContent = '';
    } catch (_) { status.textContent = dialog.dataset.error; }
  });
  document.addEventListener('submit', async event => {
    if (!event.target.matches('[data-privacy-form]')) return;
    event.preventDefault();
    const form = event.target, dialog = form.closest('dialog'), status = dialog.querySelector('[data-privacy-status]');
    const fieldset = form.querySelector('fieldset');
    const consent = Object.fromEntries([...form.querySelectorAll('input[type=checkbox]')].map(input => [input.name, input.checked]));
    if (navigator.globalPrivacyControl === true) consent.sale_of_data = false;
    fieldset.disabled = true;
    status.textContent = dialog.dataset.loading;
    try {
      const api = await loadPrivacy();
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Save timed out')), 8000);
        api.setTrackingConsent(consent, result => { clearTimeout(timeout); result?.error ? reject(new Error(result.error)) : resolve(); });
      });
      status.textContent = dialog.dataset.success;
    } catch (_) { status.textContent = dialog.dataset.error; }
    finally { fieldset.disabled = false; }
  });
})();
